import { Dialog as DialogPrimitive } from "radix-ui";
import React from "react";
import { useTranslation } from "react-i18next";

import { AccordionContent, AccordionTrigger } from "~/components/accordion";
import { ContactUsDialog } from "~/components/contact-us-dialog";
import {
  getTrackingErrors,
  mapTrackingStatusToTagShipment,
} from "~/components/order/order-parcel";
import { cn } from "~/components/ui/cn";
import type { OrderQuery } from "~/graphql/graphql-new.gen";

import { orderShipmentVariant } from "../order-status-badge/order-shipment-badge";
import { EstimatedDeliveryDates } from "./estimated-delivery-dates";
import { OrderDeliveredAt } from "./order-delivered-at";
import { OrderShipmentBadge } from "./order-shipment-badge";
import { TrackingOrdersLinked } from "./tracking-orders-linked";
import { TrackingViewLink } from "./tracking-view-link";

export type OrderMultiParcelProps = {
  index: number;
  orderId: string;
  parcel: OrderQuery["order"]["parcels"][number];
  shipping?: OrderQuery["order"]["shipping"];
};

export const OrderMultiParcel: React.FC<OrderMultiParcelProps> = ({
  index,
  parcel,
  shipping,
}: OrderMultiParcelProps) => {
  const { t } = useTranslation();

  const trackingErrors = getTrackingErrors(
    {
      hasTrackingStucked: parcel.hasTrackingStucked,
      trackingSubStatus: parcel.trackingSubStatus,
    },
    t,
  );
  const variantStyle = orderShipmentVariant({
    hasTrackingStucked: parcel.hasTrackingStucked,
    tag: mapTrackingStatusToTagShipment(parcel.trackingStatus),
    trackingSubStatus: parcel.trackingSubStatus,
  });

  return (
    <>
      <AccordionTrigger className="flex w-full items-center px-4">
        <div className="flex flex-1 items-center gap-2">
          <span className="text-gray-700">
            {t("order.parcel.label")} {index + 1}
          </span>
          <OrderShipmentBadge
            hasTrackingStucked={parcel.hasTrackingStucked}
            tag={mapTrackingStatusToTagShipment(parcel.trackingStatus)}
            trackingSubStatus={parcel.trackingSubStatus}
          />
        </div>
        <OrderDeliveredAt parcel={parcel} />
      </AccordionTrigger>

      <AccordionContent className="flex flex-col gap-4 border-t border-gray-300 px-4 pt-4">
        {trackingErrors.length > 0 ? (
          <div
            className={cn("space-y-4 rounded border p-4", {
              "border-[#F0C5C5] bg-red-50": variantStyle === "danger",
              "border-[#F6E1C2] bg-orange-300": variantStyle === "warning",
            })}
          >
            {trackingErrors.map((trackingError) => (
              <div key={trackingError.id} className="space-y-4">
                <div className="space-y-1">
                  <div className="text-sm font-bold text-gray-700">
                    {trackingError.title}
                  </div>
                  <div className="text-sm text-gray-700">
                    {trackingError.description}
                  </div>
                </div>
                {trackingError.toDo ? (
                  <div className="text-sm font-bold text-gray-700">
                    {trackingError.toDo}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div className="text-sm text-gray-700">
          {t("order.announced_delivery")}{" "}
          <EstimatedDeliveryDates
            history={shipping?.deliveryDateRangeHistory}
            estimatedDeliveryDateRange={parcel.estimatedDeliveryDateRange}
          />
        </div>

        <TrackingViewLink parcel={parcel} />
        <TrackingOrdersLinked parcel={parcel} />

        {trackingErrors.some((error) => error.showContact) ? (
          <div className="border-t border-gray-300 pt-4">
            <DialogPrimitive.Root modal={false}>
              <DialogPrimitive.Trigger asChild>
                <button type="button" className="font-semibold text-green-900">
                  {t("order.actions_contact_us_via_chat")}
                </button>
              </DialogPrimitive.Trigger>
              <ContactUsDialog />
            </DialogPrimitive.Root>
          </div>
        ) : null}
      </AccordionContent>
    </>
  );
};
